import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import VersionsTable, { type DeployEnvironment, type Version, type VersionAction } from './VersionsTable';
import type { ServiceStatus, DeploymentMetrics } from '../../types/api';

// A dispatch returns before GitHub has created the run, so an immediate
// refresh does not show it. One follow-up read after this long usually does;
// the 30-second poll catches anything slower.
const RUN_APPEARS_AFTER_MS = 5000;

const DeploymentsComponent: React.FC = () => {
  const [versions, setVersions] = useState<Version[]>([]);
  const [metrics, setMetrics] = useState<DeploymentMetrics | null>(null);
  const [serviceStatus, setServiceStatus] = useState<ServiceStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchDeploymentData();

    // A deploy takes a few minutes and its status changes underneath us, so
    // the view refreshes rather than showing whatever was true on mount.
    const interval = setInterval(fetchDeploymentData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchDeploymentData = async () => {
    setIsLoading(true);
    try {
      // Settled rather than all: the live ECS status comes from this
      // environment's own service and the history from GitHub Actions. Either
      // can be unavailable on its own, and one missing should not blank the
      // whole panel.
      const [versionsResult, metricsResult, statusResult] = await Promise.allSettled([
        apiService.getVersions(),
        apiService.getDeploymentMetrics(),
        apiService.getDeploymentStatus(),
      ]);

      if (versionsResult.status === 'fulfilled') {
        const data = versionsResult.value.data as { versions?: Version[]; warnings?: string[] } | undefined;
        setVersions(data?.versions ?? []);
        // A partial list is shown, but not as if it were complete: the backend
        // names what it could not read — usually a token permission.
        const warnings = data?.warnings ?? [];
        setError(warnings.length > 0 ? warnings.join(' ') : null);
      } else {
        setError(versionsResult.reason?.message ?? 'Could not load versions');
      }

      if (metricsResult.status === 'fulfilled') {
        setMetrics(metricsResult.value.data ?? null);
      }

      if (statusResult.status === 'fulfilled') {
        setServiceStatus(statusResult.value.data ?? null);
      } else {
        setServiceStatus(null);
      }
    } catch (err) {
      console.error('Error fetching deployment data:', err);
      setError(err instanceof Error ? err.message : 'Could not load deployment data');
    } finally {
      setIsLoading(false);
    }
  };

  const triggerDeployment = async (environment: string) => {
    // Production is a real release to real customers. The confirm is not
    // ceremony — this button is two clicks away from the order flow.
    if (environment === 'production') {
      const ok = window.confirm(
        'Request a production deploy?\n\n' +
        'This promotes the commit that staging most recently deployed successfully. ' +
        'Nobody approves it after this: once requested, it ships to customers.',
      );
      if (!ok) return;
    }

    setBusy(true);
    setNotice(null);
    try {
      const response = await apiService.triggerDeployment(environment);
      setNotice(response.message ?? `Deployment to ${environment} requested`);
      await fetchDeploymentData();
    } catch (err) {
      console.error('Error triggering deployment:', err);
      setError(err instanceof Error ? err.message : 'Could not trigger the deployment');
    } finally {
      setBusy(false);
    }
  };

  // Every row button is the same request — dispatch that environment's
  // workflow with this commit. What differs is what the person is told first.
  const deployVersion = async (version: Version, environment: DeployEnvironment, action: VersionAction) => {
    const what = `${version.shortSha} — ${version.message}`;
    const onBranch = version.onMain ? '' : `\n\nThis is ${version.branch || 'a branch'}, not main.`;

    const question = {
      staging: {
        deploy: `Deploy ${what} to staging?${onBranch}\n\nStaging is rebuilt from this commit and replaces what it runs now.`,
        rollback: `Roll staging back to ${what}?\n\nThis redeploys that exact commit through the pipeline, replacing the newer version staging runs now.`,
      },
      production: {
        deploy: `Promote ${what} to production?\n\nThis ships the image staging ran for this commit. Nobody approves it after this: once requested, it reaches customers.`,
        rollback: `Roll production back to ${what}?\n\nThis redeploys that exact commit to customers through the pipeline, replacing the newer version production runs now. Nobody approves it after this.`,
      },
    }[environment][action];

    if (!window.confirm(question)) return;

    setBusy(true);
    setNotice(null);
    try {
      await apiService.triggerDeployment(environment, version.sha);
      const verb = action === 'rollback' ? 'Rollback of' : 'Deploy of';
      setNotice(`${verb} ${environment} to ${version.shortSha} requested. It appears below once the pipeline picks it up.`);
      await fetchDeploymentData();
      setTimeout(fetchDeploymentData, RUN_APPEARS_AFTER_MS);
    } catch (err) {
      console.error(`Error requesting ${environment} ${action}:`, err);
      setError(err instanceof Error ? err.message : `Could not request the ${environment} ${action}`);
    } finally {
      setBusy(false);
    }
  };

  const rollbackDeployment = async () => {
    const ok = window.confirm(
      'Roll back to the previous release?\n\n' +
      'This moves the running service back one task definition revision, immediately. ' +
      'It does not wait for a pipeline or an approval.\n\n' +
      'To go back to a specific version instead, use Roll back on its row below.',
    );
    if (!ok) return;

    setBusy(true);
    setNotice(null);
    try {
      const response = await apiService.rollbackDeployment();
      setNotice(response.message ?? 'Rollback started');
      await fetchDeploymentData();
    } catch (err) {
      console.error('Error rolling back deployment:', err);
      setError(err instanceof Error ? err.message : 'Could not roll back');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Deployment Overview */}
      {metrics && (
        <div className="grid md:grid-cols-4 gap-6">
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Total Deployments</h3>
            <div className="text-3xl font-bold text-brand-blue">{metrics.totalDeployments}</div>
            <p className="text-gray-500 text-sm">All time</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Successful</h3>
            <div className="text-3xl font-bold text-scarf-green">{metrics.successful}</div>
            <p className="text-gray-500 text-sm">Last 30 days</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Failed</h3>
            <div className="text-3xl font-bold text-brand-red">{metrics.failed}</div>
            <p className="text-gray-500 text-sm">Last 30 days</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Avg Duration</h3>
            <div className="text-3xl font-bold text-brand-blue-light">{metrics.averageDuration}s</div>
            <p className="text-gray-500 text-sm">Per deployment</p>
          </div>
        </div>
      )}

      {/* Live state of this environment, straight from ECS. */}
      {serviceStatus && (
        <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-title font-bold text-deep-navy">
              Running now &mdash; {serviceStatus.environment}
            </h3>
            {serviceStatus.rolloutState && (
              <span className={`px-3 py-1 rounded text-xs font-bold ${
                serviceStatus.rolloutState === 'COMPLETED'
                  ? 'bg-scarf-green/20 text-scarf-green'
                  : serviceStatus.rolloutState === 'FAILED'
                    ? 'bg-brand-red/20 text-brand-red'
                    : 'bg-brand-blue-light/20 text-brand-blue-light'
              }`}>
                {serviceStatus.rolloutState}
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Tasks</p>
              <p className="font-bold text-deep-navy">
                {serviceStatus.runningCount} / {serviceStatus.desiredCount}
                {serviceStatus.pendingCount > 0 && ` (+${serviceStatus.pendingCount} starting)`}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Commit</p>
              <p className="font-bold text-deep-navy font-mono">
                {serviceStatus.deployedCommit?.slice(0, 7) ?? 'unknown'}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Task definition</p>
              <p className="font-bold text-deep-navy font-mono">{serviceStatus.taskDefinition ?? 'unknown'}</p>
            </div>
            <div>
              <p className="text-gray-500">Service</p>
              <p className="font-bold text-deep-navy">{serviceStatus.service}</p>
            </div>
          </div>

          {serviceStatus.rolloutState === 'FAILED' && serviceStatus.rolloutStateReason && (
            <p className="mt-4 text-sm text-brand-red">
              {serviceStatus.rolloutStateReason}
            </p>
          )}
        </div>
      )}

      {/* Quick Actions.
          One commit builds all three applications, so there is nothing to
          deploy per project any more — the axis that matters is which
          environment receives it. */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-2">Deployment Actions</h3>
        <p className="text-gray-500 text-sm mb-4">
          Staging deploys automatically when CI passes on main. The buttons here act on the
          latest: deploy main's tip to staging, or promote what staging last deployed. To
          deploy, promote or roll back a specific version, use its row under Versions. There
          is no approval step after any button, so it ships as soon as the pipeline finishes.
        </p>

        {notice && (
          <div className="mb-4 px-4 py-3 rounded-xl bg-scarf-green/10 text-scarf-green text-sm font-bold">
            {notice}
          </div>
        )}

        {error && (
          <div className="mb-4 px-4 py-3 rounded-xl bg-brand-red/10 text-brand-red text-sm font-bold">
            {error}
          </div>
        )}

        <div className="flex flex-wrap gap-4">
          <button
            onClick={() => triggerDeployment('staging')}
            disabled={busy}
            className="bg-brand-blue-light hover:bg-brand-blue disabled:opacity-50 text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            Deploy to staging
          </button>
          <button
            onClick={() => triggerDeployment('production')}
            disabled={busy}
            className="bg-brand-red hover:bg-brand-red/90 disabled:opacity-50 text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            Promote to production
          </button>
          <button
            onClick={rollbackDeployment}
            disabled={busy || !serviceStatus}
            className="bg-deep-navy hover:bg-deep-navy/90 disabled:opacity-50 text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            Roll back this environment
          </button>
          <button
            onClick={fetchDeploymentData}
            disabled={isLoading}
            className="bg-frost-white hover:bg-ice-teal-light disabled:opacity-50 text-deep-navy border border-ice-teal-light font-bold px-6 py-3 rounded-xl transition-colors"
          >
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* One row per version, deployed or not, each with its own actions. */}
      <VersionsTable versions={versions} busy={busy} onAction={deployVersion} />

      {/* Deployment Statistics */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Deployment Statistics</h3>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="text-center">
            <h4 className="text-deep-navy font-bold mb-2">Success Rate</h4>
            <div className="text-3xl font-bold text-scarf-green">
              {metrics ? Math.round((metrics.successful / metrics.totalDeployments) * 100) : 0}%
            </div>
            <p className="text-gray-500 text-sm">Last 30 days</p>
          </div>
          <div className="text-center">
            <h4 className="text-deep-navy font-bold mb-2">Average Duration</h4>
            <div className="text-3xl font-bold text-brand-blue-light">
              {metrics ? metrics.averageDuration : 0}s
            </div>
            <p className="text-gray-500 text-sm">Per deployment</p>
          </div>
          <div className="text-center">
            <h4 className="text-deep-navy font-bold mb-2">Last Deployment</h4>
            <div className="text-lg font-bold text-brand-red">
              {metrics ? new Date(metrics.lastDeployment).toLocaleDateString() : 'N/A'}
            </div>
            <p className="text-gray-500 text-sm">Production</p>
          </div>
        </div>
      </div>

      {/* Deployment Best Practices */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Deployment Best Practices</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-scarf-green font-bold mb-3">Before Deployment</h4>
            <ul className="text-gray-600 space-y-2">
              <li>Run all tests successfully</li>
              <li>Review code changes</li>
              <li>Check environment configuration</li>
              <li>Verify database migrations</li>
            </ul>
          </div>
          <div>
            <h4 className="text-scarf-green font-bold mb-3">After Deployment</h4>
            <ul className="text-gray-600 space-y-2">
              <li>Monitor application health</li>
              <li>Check error logs</li>
              <li>Verify functionality</li>
              <li>Update documentation</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeploymentsComponent;
